// Cloudflare Turnstile for respondent submissions. Each token is single-use, so
// every submission attempt (including offline-queue retries) asks for a new one.
// The widget stays invisible unless Cloudflare needs the person to click.

interface TurnstileApi {
  render(el: HTMLElement, opts: Record<string, unknown>): string;
  reset(id: string): void;
  remove(id: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
let scriptLoad: Promise<TurnstileApi> | null = null;
let chain: Promise<unknown> = Promise.resolve();

function loadScript(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  scriptLoad ??= new Promise<TurnstileApi>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = SCRIPT_URL;
    s.async = true;
    s.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error('turnstile unavailable')));
    s.onerror = () => {
      scriptLoad = null;
      s.remove();
      reject(new Error('turnstile script failed to load'));
    };
    document.head.appendChild(s);
  });
  return scriptLoad;
}

function solve(siteKey: string, language: string): Promise<string> {
  return loadScript().then(
    (ts) =>
      new Promise<string>((resolve, reject) => {
        const host = document.createElement('div');
        host.className = 'turnstile-host';
        document.body.appendChild(host);
        let id = '';
        const done = (fn: () => void) => {
          clearTimeout(timer);
          if (id) ts.remove(id);
          host.remove();
          fn();
        };
        // Give up if nobody completes an interactive challenge.
        const timer = setTimeout(() => done(() => reject(new Error('turnstile timed out'))), 120_000);
        id = ts.render(host, {
          sitekey: siteKey,
          appearance: 'interaction-only',
          language,
          callback: (token: string) => done(() => resolve(token)),
          'error-callback': () => done(() => reject(new Error('turnstile error'))),
          'expired-callback': () => done(() => reject(new Error('turnstile expired'))),
          'timeout-callback': () => done(() => reject(new Error('turnstile timed out'))),
        });
      }),
  );
}

export interface VisibleWidget {
  /** Clears the current token and runs a new challenge (tokens are single-use). */
  reset(): void;
  remove(): void;
}

/**
 * Renders an always-visible widget into el, unless cancelled() turns true while the
 * script loads (resolves null then). onToken(null) means the token expired or failed.
 */
export async function renderTurnstile(
  el: HTMLElement,
  siteKey: string,
  language: string,
  onToken: (token: string | null) => void,
  cancelled: () => boolean,
): Promise<VisibleWidget | null> {
  const ts = await loadScript();
  if (cancelled()) return null;
  const id = ts.render(el, {
    sitekey: siteKey,
    language,
    theme: 'auto',
    size: 'flexible',
    callback: (token: string) => onToken(token),
    'expired-callback': () => onToken(null),
    'error-callback': () => onToken(null),
  });
  return { reset: () => ts.reset(id), remove: () => ts.remove(id) };
}

/** A fresh Turnstile token. Calls are serialized so only one widget shows at a time. */
export function turnstileToken(siteKey: string, language = 'auto'): Promise<string> {
  const next = chain.then(() => solve(siteKey, language));
  chain = next.catch(() => undefined);
  return next;
}
